import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import LiveDataFeed from './components/LiveDataFeed';
import FieldMappingPanel from './components/FieldMappingPanel';
import SyncMonitor from './components/SyncMonitor';
import DataQualityPanel from './components/DataQualityPanel';
import { wearableConnectionService, wearableDataService } from '../../services/wearablesService';
import { useAuth } from '../../contexts/AuthContext';

const RealTimeDataSync = () => {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [liveData, setLiveData] = useState({});
  const [syncStatus, setSyncStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [syncInterval, setSyncInterval] = useState(null);

  useEffect(() => {
    if (user) {
      loadDevices();
    }
  }, [user]);

  useEffect(() => {
    if (autoSync && devices?.length > 0) {
      startAutoSync();
    } else {
      stopAutoSync();
    }

    return () => stopAutoSync();
  }, [autoSync, devices]);

  const loadDevices = async () => {
    setLoading(true);
    const { data, error } = await wearableConnectionService?.getConnections(user?.id);
    if (!error && data) {
      const activeDevices = data?.filter(d => d?.isActive);
      setDevices(activeDevices);
    }
    setLoading(false);
  };

  const startAutoSync = () => {
    if (syncInterval) return;

    const interval = setInterval(() => {
      syncAllDevices();
    }, 30000); // Sync every 30 seconds

    setSyncInterval(interval);
    syncAllDevices(); // Initial sync
  };

  const stopAutoSync = () => {
    if (syncInterval) {
      clearInterval(syncInterval);
      setSyncInterval(null);
    }
  };

  const syncAllDevices = async () => {
    for (const device of devices) {
      await syncDevice(device?.id);
    }
  };

  const syncDevice = async (deviceId) => {
    setSyncStatus(prev => ({ ...prev, [deviceId]: 'syncing' }));

    const { data, error } = await wearableDataService?.syncDeviceData(deviceId);

    if (!error && data) {
      setLiveData(prev => ({ ...prev, [deviceId]: data }));
      setSyncStatus(prev => ({ ...prev, [deviceId]: 'success' }));
    } else {
      setSyncStatus(prev => ({ ...prev, [deviceId]: 'error' }));
    }

    setTimeout(() => {
      setSyncStatus(prev => ({ ...prev, [deviceId]: null }));
    }, 3000);
  };

  const handleManualSync = async () => {
    await syncAllDevices();
  };

  const handleForceSync = async (deviceId) => {
    await syncDevice(deviceId);
  };

  const getTotalDataPoints = () => {
    return Object.values(liveData)?.reduce((total, deviceData) => {
      return total + Object.keys(deviceData || {})?.length;
    }, 0);
  };

  const getAverageSyncTime = () => {
    return '2.3s'; // Mock average
  };

  const getDataQuality = () => {
    const totalDevices = devices?.length;
    const successfulDevices = Object.values(syncStatus)?.filter(s => s === 'success')?.length;
    return totalDevices > 0 ? ((successfulDevices / totalDevices) * 100)?.toFixed(0) : 100;
  };

  return (
    <>
      <Helmet>
        <title>Real-time Data Sync - FormForge</title>
        <meta name="description" content="Monitor and manage real-time wearable device data synchronization" />
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-6 lg:py-8">
          <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>Real-time Data Sync</h1>
                <p className="text-lg" style={{ color: '#6B7280' }}>Monitor live wearable data streams and field mappings</p>
              </div>
              <div className="flex items-center space-x-3">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" checked={autoSync} onChange={(e) => setAutoSync(e?.target?.checked)} className="w-5 h-5 rounded" style={{ accentColor: '#FF6B00' }} />
                  <span className="text-sm font-medium" style={{ color: '#E8EAED' }}>Auto Sync</span>
                </label>
                <Button onClick={handleManualSync} className="flex items-center space-x-2" disabled={devices?.length === 0}>
                  <Icon name="RefreshCw" size={20} />
                  <span>Sync Now</span>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Sync Overview Stats */}
          <motion.div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            {[
              { label: 'Active Devices', value: devices?.length, icon: 'Watch', color: '#00D4FF', note: 'All syncing', noteColor: '#AAFF00' },
              { label: 'Data Points', value: getTotalDataPoints(), icon: 'Database', color: '#AAFF00', note: 'Last sync', noteColor: '#6B7280' },
              { label: 'Avg Sync Time', value: getAverageSyncTime(), icon: 'Clock', color: '#FF6B00', note: '-0.5s faster', noteColor: '#AAFF00' },
              { label: 'Data Quality', value: `${getDataQuality()}%`, icon: 'CheckCircle', color: '#AAFF00', note: 'No errors', noteColor: '#6B7280' }
            ]?.map((stat) => (
              <div key={stat?.label} className="rounded-xl p-6" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm" style={{ color: '#6B7280' }}>{stat?.label}</span>
                  <Icon name={stat?.icon} size={18} style={{ color: stat?.color }} />
                </div>
                <p className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: stat?.color }}>{stat?.value}</p>
                <p className="text-xs mt-1" style={{ color: stat?.noteColor }}>{stat?.note}</p>
              </div>
            ))}
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#FF6B00' }}></div>
            </div>
          ) : devices?.length === 0 ? (
            <motion.div className="rounded-xl p-12 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Icon name="Watch" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
              <h3 className="text-xl font-semibold mb-2" style={{ color: '#E8EAED' }}>No Active Devices</h3>
              <p className="mb-6" style={{ color: '#6B7280' }}>Connect devices in the Device Integration Hub to start syncing data</p>
              <Button onClick={() => window.location.href = '/device-integration-hub'}>
                <Icon name="Plus" size={20} className="mr-2" />
                Go to Device Hub
              </Button>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
                <LiveDataFeed devices={devices} liveData={liveData} syncStatus={syncStatus} onForceSync={handleForceSync} />
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
                <FieldMappingPanel devices={devices} />
              </motion.div>
              <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <SyncMonitor devices={devices} syncStatus={syncStatus} />
              </motion.div>
              <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <DataQualityPanel liveData={liveData} devices={devices} />
              </motion.div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default RealTimeDataSync;
